import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { MaskedLines, Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { WaveMark, LaurelIllu } from "@/components/Illustrations";
import { TESTIMONIALS, WA_VALORACION, waKarina } from "@/lib/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.35]);

  return (
    <section ref={ref} className="relative bg-ink text-cream min-h-screen grid lg:grid-cols-2 overflow-hidden" data-testid="hero-section">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 pt-32 pb-16 lg:py-0 relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-gold mb-8"
        >
          Micropigmentación · La Gomera
        </motion.p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight" data-testid="hero-title">
          <MaskedLines
            lines={["Cejas y labios", "con criterio."]}
            delay={0.35}
            lastLineClassName="text-gold italic"
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-md text-base leading-relaxed text-cream/70"
          data-testid="hero-subtitle"
        >
          Técnica NAGI — resultados naturales y comprobados, no un tatuaje. Sin dolor, adaptada a pieles
          sensibles al sol, acompañándote en cada paso del proceso.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href={WA_VALORACION}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-reserva-button"
            className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300"
          >
            Reserva tu valoración <ArrowRight size={15} />
          </a>
          <Link
            to="/tecnica-nagi"
            data-testid="hero-tecnica-button"
            className="inline-flex items-center gap-3 rounded-full border border-cream/30 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-cream hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Conoce la Técnica NAGI
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 hidden lg:block"
        >
          <WaveMark className="w-32 text-gold/50" />
        </motion.div>
      </div>

      <motion.div style={{ y, opacity }} className="relative flex items-center justify-center px-6 pb-16 lg:pb-0 lg:pr-20 pt-6 lg:pt-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -inset-6 sm:-inset-10 border border-gold/30 pointer-events-none" />
          <img
            src="/images/logo-light.png"
            alt="Karina Méndez Studio — logo"
            data-testid="hero-portrait"
            className="max-h-[40vh] lg:max-h-[58vh] w-auto mx-auto drop-shadow-[0_0_70px_rgba(197,160,89,0.18)]"
          />
          <WaveMark className="w-28 text-gold/50 mx-auto mt-10" />
        </motion.div>
      </motion.div>
    </section>
  );
};

const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <Marquee />

    <section className="bg-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-32" data-testid="home-quien-soy">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHead num="01" eyebrow="Quién soy" title={<>Crear, creer, <span className="italic text-gold">crecer.</span></>} />
        </div>
        <div className="lg:col-span-6 lg:col-start-6">
          <Reveal delay={0.15}>
            <p className="font-serif text-xl sm:text-2xl leading-relaxed text-ink/85">
              Nací en La Guaira, Venezuela, y desde muy joven encontré en la belleza una forma de construir.
              Hoy soy la creadora de la Técnica NAGI, y mi propósito es simple: seguir creciendo, y ayudar a
              otras a construir — con criterio, sin ruido.
            </p>
            <Link
              to="/sobre-mi"
              data-testid="home-conoceme-link"
              className="inline-flex items-center gap-2 mt-8 font-mono text-xs uppercase tracking-[0.2em] text-gold gold-underline"
            >
              Conóceme más <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-32 relative overflow-hidden" data-testid="home-nagi">
      <div className="max-w-4xl">
        <SectionHead dark num="02" eyebrow="Técnica NAGI" title={<span className="italic font-light">“El momento exacto en que el viento se detiene y el mar queda perfectamente en calma.”</span>} />
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-cream/70 leading-relaxed">
            Micropigmentación de cejas y labios con resultados naturales y comprobados — sin efecto tatuaje,
            diseñada para pieles sensibles al sol.
          </p>
          <Link
            to="/tecnica-nagi"
            data-testid="home-descubre-tecnica-link"
            className="inline-flex items-center gap-3 mt-10 rounded-full border border-gold/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            Descubre la técnica <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
      <WaveMark className="absolute -right-10 bottom-8 w-72 text-gold/10 pointer-events-none" />
    </section>

    <section className="bg-beige px-6 sm:px-12 lg:px-20 py-20 lg:py-32" data-testid="home-servicios">
      <SectionHead num="03" eyebrow="Servicios destacados" title={<>Lo que puedo <span className="italic text-gold">hacer por ti</span></>} />
      <div className="grid md:grid-cols-3 gap-px bg-sand mt-14 border border-sand">
        {[
          { id: "brows", name: "NAGI Brows", desc: "Cejas definidas con la forma perfecta para tu rostro — despierta ya con las cejas hechas, sin lápiz ni gel cada mañana.", img: "/images/nagi-brows.jpg", tema: "NAGI Brows" },
          { id: "lips", name: "NAGI Lips", desc: "Color y definición natural, sin efecto pintado ni contorno artificial. Labios con vida propia, sin depender del labial.", img: "/images/nagi-lips.webp", tema: "NAGI Lips" },
          { id: "otros", name: "Manicura · Pestañas · Laminado", desc: "Manicura rusa PRO, lifting de pestañas, laminado y diseño de cejas con henna — el cuidado completo en un solo espacio.", img: "/images/manicura-1.jpg", tema: "servicios de manicura y pestañas" },
        ].map(({ id, name, desc, img, tema }, i) => (
          <Reveal key={id} delay={i * 0.12} className="bg-beige">
            <div className="group p-8 lg:p-10 h-full flex flex-col hover:bg-cream transition-colors duration-500" data-testid={`home-service-${id}`}>
              <div className="overflow-hidden mb-8">
                <img
                  src={img}
                  alt={name}
                  data-testid={`home-service-${id}-img`}
                  className="w-full h-auto group-hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <h3 className="font-serif text-2xl mb-4">{name}</h3>
              <p className="text-sm leading-relaxed text-ink/65 flex-1">{desc}</p>
              <a
                href={waKarina(tema)}
                target="_blank"
                rel="noreferrer"
                data-testid={`home-service-${id}-cta`}
                className="inline-flex items-center gap-2 mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-gold gold-underline self-start"
              >
                Consultar <ArrowUpRight size={13} />
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-32" data-testid="home-academia">
      <div className="border border-gold/30 p-8 sm:p-14 lg:p-20 grid lg:grid-cols-2 gap-12 items-center relative overflow-hidden">
        <div>
          <Eyebrow dark>04 — Academia Karina Méndez · Próximamente</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-6 leading-tight">
            No enseño una técnica. <span className="italic text-gold">Formo criterio.</span>
          </h2>
          <p className="mt-6 text-cream/70 leading-relaxed max-w-md">
            Fórmate conmigo desde cero — sin experiencia previa — y conviértete en la profesional que otras
            buscan. La Academia está casi lista, y quiero que seas la primera en saberlo.
          </p>
          <Link
            to="/academia"
            data-testid="home-academia-cta"
            className="inline-flex items-center gap-3 mt-10 rounded-full bg-gold text-ink px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300"
          >
            Quiero formarme <ArrowRight size={15} />
          </Link>
        </div>
        <Reveal delay={0.2} className="hidden lg:flex justify-end">
          <LaurelIllu className="w-72 text-gold/70" />
        </Reveal>
      </div>
    </section>

    <section className="bg-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-32" data-testid="home-testimonios">
      <SectionHead num="05" eyebrow="Testimonios reales · Google" title={<>Ellas ya lo <span className="italic text-gold">viven</span></>} />
      <div className="grid md:grid-cols-3 gap-8 mt-14">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.initials} delay={i * 0.12}>
            <figure className="border border-sand bg-beige p-8 lg:p-10 h-full flex flex-col" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 text-gold mb-6" aria-label="5 estrellas">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="font-serif text-lg leading-relaxed text-ink/85 flex-1">“{t.text}”</blockquote>
              <figcaption className="flex items-center gap-4 mt-8">
                <span className="w-11 h-11 rounded-full bg-ink text-gold font-serif text-sm flex items-center justify-center ring-1 ring-gold/50" data-testid={`testimonial-avatar-${i}`}>
                  {t.initials}
                </span>
                <span className="text-sm font-medium tracking-wide">{t.name}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-24 lg:py-36 text-left relative overflow-hidden" data-testid="home-cta-final">
      <Reveal>
        <Eyebrow dark>El siguiente paso es tuyo</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 max-w-3xl">
          Tu rostro merece <span className="italic text-gold">criterio</span>, no tendencias.
        </h2>
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={WA_VALORACION}
            target="_blank"
            rel="noreferrer"
            data-testid="home-final-reserva-button"
            className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300"
          >
            Reserva tu valoración gratuita <ArrowRight size={15} />
          </a>
          <Link
            to="/academia"
            data-testid="home-final-academia-button"
            className="inline-flex items-center gap-3 rounded-full border border-cream/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-cream hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Quiero formarme
          </Link>
        </div>
      </Reveal>
      <WaveMark className="absolute right-0 top-10 w-80 text-gold/10 pointer-events-none" />
    </section>
  </div>
);

export default Home;
